// ── Voice preferences ─────────────────────────────────────────────────────────
// Which voice answers are read in and how fast — plus whether the voice has
// been offered to them yet. There is no language setting: the speech service
// detects what the user speaks, so any language works without picking one.
//
// **Kept on the account** (MyAMP's /api/web/voice/settings, shared with the
// mobile app), so a choice made on the phone is the one the web uses and the
// other way round, and signing out — which clears the browser's storage —
// loses nothing. Loaded once per page and held here; the Settings page and the
// voice page ask for a fresh copy when they open, so a change made elsewhere
// shows up. A change is applied here at once and saved behind it.
//
// **An unset voice means "the first stock voice"**, resolved when needed rather
// than saved — saving it would look like a choice the user made, and would
// outlive the voice being removed. Same rule as the phone.

import {
  getVoiceSettings,
  listVoices,
  updateVoiceSettings,
  type VoiceOption,
  type VoiceSettings,
} from '../voiceApi';

/** What the speech service accepts (0.7–1.2); 1.0 is the voice's own pace. */
export const MIN_SPEED = 0.7;
export const MAX_SPEED = 1.2;
export const DEFAULT_SPEED = 1.0;

/** Offered in settings. 0.7 is allowed by the clamp but not offered — nobody here wants slower than 0.8. */
export const SPEED_OPTIONS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 0.8, label: 'Slower (0.8×)' },
  { value: 1.0, label: 'Normal (1.0×)' },
  { value: 1.1, label: 'Faster (1.1×)' },
  { value: 1.2, label: 'Fastest (1.2×)' },
];

const DEFAULTS: VoiceSettings = { voiceId: '', speed: DEFAULT_SPEED, voiceChoiceOffered: false };

let current: VoiceSettings = { ...DEFAULTS };
let loading: Promise<VoiceSettings> | null = null;

function normalise(settings: Partial<VoiceSettings>): VoiceSettings {
  const speed = Number(settings.speed);
  return {
    voiceId: settings.voiceId || '',
    speed: Number.isFinite(speed) ? Math.min(MAX_SPEED, Math.max(MIN_SPEED, speed)) : DEFAULT_SPEED,
    voiceChoiceOffered: !!settings.voiceChoiceOffered,
    isDefault: settings.isDefault,
  };
}

// ── Settings this browser held before they moved to the account ───────────────
// Moved up once, the first time an account with nothing saved is loaded here,
// then removed — the account is the only copy from then on.
const LEGACY_KEYS = {
  voiceId: 'voiceChat.voiceId',
  speed: 'voiceChat.speed',
  // Only cleared now: the language setting is gone.
  language: 'voiceChat.language',
  voiceChoiceOffered: 'voiceChat.voiceChoiceOffered',
} as const;

function takeLegacySettings(): Partial<VoiceSettings> | null {
  try {
    const voiceId = localStorage.getItem(LEGACY_KEYS.voiceId);
    const speed = localStorage.getItem(LEGACY_KEYS.speed);
    const offered = localStorage.getItem(LEGACY_KEYS.voiceChoiceOffered);
    Object.values(LEGACY_KEYS).forEach(key => localStorage.removeItem(key));
    const legacy: Partial<VoiceSettings> = {};
    if (voiceId) legacy.voiceId = voiceId;
    if (speed && Number.isFinite(parseFloat(speed))) legacy.speed = parseFloat(speed);
    if (offered === 'true') legacy.voiceChoiceOffered = true;
    return Object.keys(legacy).length ? legacy : null;
  } catch {
    return null;
  }
}

/**
 * The user's settings. Fetched once per page load; `fresh` asks the account
 * again (when a screen that shows them opens). A failed load answers with what
 * is held — the defaults, before anything has loaded — and is tried again next time.
 */
export function loadVoiceSettings(fresh = false): Promise<VoiceSettings> {
  if (loading && !fresh) return loading;
  const request: Promise<VoiceSettings> = fetchVoiceSettings().catch(() => {
    if (loading === request) loading = null;
    return current;
  });
  loading = request;
  return request;
}

async function fetchVoiceSettings(): Promise<VoiceSettings> {
  let settings = normalise(await getVoiceSettings());
  const legacy = takeLegacySettings();
  if (legacy && settings.isDefault) settings = normalise(await updateVoiceSettings(legacy));
  current = settings;
  return settings;
}

/** What's held now — the defaults until the first load lands. */
export function voiceSettingsSnapshot(): VoiceSettings {
  return current;
}

/**
 * Changes some settings: applied here at once (so the next answer uses them),
 * then saved to the account. Rejects when the save fails, with what's held put
 * back, so the caller can say so.
 */
export async function saveVoiceSettings(changes: Partial<Omit<VoiceSettings, 'isDefault'>>): Promise<VoiceSettings> {
  const before = current;
  current = normalise({ ...current, ...changes, isDefault: false });
  try {
    current = normalise(await updateVoiceSettings(changes));
    loading = Promise.resolve(current);
    return current;
  } catch (err) {
    current = before;
    throw err;
  }
}

export function preferredSpeed(): number {
  return current.speed;
}

let catalogue: Promise<VoiceOption[]> | null = null;

/** The voice list, fetched once per page load. `refresh` asks past the server's cache too. */
export function voiceCatalogue(refresh = false): Promise<VoiceOption[]> {
  if (!catalogue || refresh) {
    catalogue = listVoices(refresh).catch(err => {
      catalogue = null; // try again next time rather than remember the failure
      throw err;
    });
  }
  return catalogue;
}

/** The user's voice, or the first in the catalogue; '' if there are none (nothing will be read aloud). */
export async function resolveVoiceId(): Promise<string> {
  const { voiceId } = await loadVoiceSettings();
  if (voiceId) return voiceId;
  try {
    const voices = await voiceCatalogue();
    return voices[0]?.voiceId ?? '';
  } catch {
    return '';
  }
}

/**
 * A voice as a row shows it, the phone's way: the name cut at its tagline
 * ("Roger - Laid-Back, Casual" → "Roger"; the dash needs a space after it, so
 * "Down-to-Earth" stays whole), and its traits — category unless it's stock,
 * then accent, gender and the descriptive label — with snake_case turned to
 * words and repeats dropped.
 */
export function describeVoice(voice: VoiceOption): { shortName: string; traits: string } {
  const cut = voice.name.search(/-\s/);
  const shortName = (cut > 0 ? voice.name.slice(0, cut) : voice.name).trim() || voice.name.trim();

  const raw = [
    voice.category && voice.category !== 'premade' ? voice.category : '',
    voice.labels?.accent,
    voice.labels?.gender,
    voice.labels?.descriptive,
  ];
  const seen = new Set<string>();
  const traits: string[] = [];
  for (const value of raw) {
    const word = (value || '').replace(/_/g, ' ').trim();
    if (!word || seen.has(word.toLowerCase())) continue;
    seen.add(word.toLowerCase());
    traits.push(word);
  }
  return { shortName, traits: traits.join(' · ') };
}

/** For tests: forget what's held, as a fresh page load would. */
export function resetVoiceSettingsForTests(): void {
  current = { ...DEFAULTS };
  loading = null;
  catalogue = null;
}
